import type { Phoneme, PhonemeType } from "@/generated/graphql";
import { PhonemeCard } from "./PhonemeCard";

interface PhonemesGridProps {
  phonemes: Phoneme[];
  loading: boolean;
  error?: unknown;
  disabled: boolean;
  onPhonemeClick: (phoneme: Phoneme) => void;
  onEditPhoneme?: (phoneme: Phoneme) => void;
  onDeletePhoneme?: (phoneme: Phoneme) => void;
  showGrouped: boolean;
}

// helper to format phoneme type labels
const formatPhonemeTypeLabel = (type: PhonemeType): string => {
  const labels: Record<PhonemeType, string> = {
    consonant: "Consonants",
    short_vowel: "Short Vowels",
    long_vowel: "Long Vowels",
    digraph: "Digraphs",
    trigraph: "Trigraphs",
    blend: "Blends",
    diphthong: "Diphthongs",
    r_controlled: "R-Controlled",
    vowel_team: "Vowel Teams",
    silent_letter: "Silent Letters",
    variant_vowel: "Variant Vowels",
    schwa: "Schwa",
    soft_hard: "Soft/Hard",
    special_pattern: "Special Patterns",
    other: "Other",
  };
  return labels[type] || type;
};

// Group phonemes by type
const groupPhonemesByType = (phonemes: Phoneme[]): Map<PhonemeType, Phoneme[]> => {
  const groups = new Map<PhonemeType, Phoneme[]>();

  for (const phoneme of phonemes) {
    const existing = groups.get(phoneme.phonemeType);
    if (existing) {
      existing.push(phoneme);
    } else {
      groups.set(phoneme.phonemeType, [phoneme]);
    }
  }

  return groups;
};

export function PhonemesGrid({
  phonemes,
  loading,
  error,
  disabled,
  onPhonemeClick,
  onEditPhoneme,
  onDeletePhoneme,
  showGrouped,
}: PhonemesGridProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-slate-500">Loading phonemes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-sm text-red-600 mb-2">Failed to load phonemes</div>
        <div className="text-xs text-slate-500">
          {error instanceof Error ? error.message : "An error occurred"}
        </div>
      </div>
    );
  }

  if (phonemes.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-slate-500">No phonemes found</div>
      </div>
    );
  }

  // show grouped by type when "All" is selected
  if (showGrouped) {
    const groupedPhonemes = groupPhonemesByType(phonemes);

    return (
      <div className="space-y-6">
        {Array.from(groupedPhonemes.entries()).map(([type, groupPhonemes]) => (
          <div key={type}>
            {/* Section Header */}
            <h3 className="text-sm font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-200">
              {formatPhonemeTypeLabel(type)}
              <span className="ml-2 text-xs font-normal text-slate-500">
                ({groupPhonemes.length})
              </span>
            </h3>

            {/* Phonemes Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {groupPhonemes.map((phoneme) => (
                <PhonemeCard
                  key={phoneme.id}
                  phoneme={phoneme}
                  onClick={() => onPhonemeClick(phoneme)}
                  disabled={disabled}
                  {...(onEditPhoneme ? { onEdit: () => onEditPhoneme(phoneme) } : {})}
                  {...(onDeletePhoneme ? { onDelete: () => onDeletePhoneme(phoneme) } : {})}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // regular flat grid for filtered view
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {phonemes.map((phoneme) => (
        <PhonemeCard
          key={phoneme.id}
          phoneme={phoneme}
          onClick={() => onPhonemeClick(phoneme)}
          disabled={disabled}
          {...(onEditPhoneme ? { onEdit: () => onEditPhoneme(phoneme) } : {})}
          {...(onDeletePhoneme ? { onDelete: () => onDeletePhoneme(phoneme) } : {})}
        />
      ))}
    </div>
  );
}
